/*
 * print-fit.js — «поместится ли эта картинка на бумагу такого размера».
 *
 * Два независимых блока, оба чистые и без DOM:
 *
 *   1. АРИФМЕТИКА ПЕЧАТИ — сколько пикселей нужно на лист заданного размера,
 *      сколько dpi реально даёт файл, во сколько раз не хватает, какой самый
 *      большой лист файл закрывает честно.
 *
 *   2. ФИЗИЧЕСКАЯ ПЛОТНОСТЬ В ФАЙЛЕ — прочитать и ЗАПИСАТЬ dpi в сам байтовый
 *      поток (JPEG APP0/JFIF, PNG pHYs). Canvas.toBlob() всегда пишет 72 dpi
 *      (JPEG) или вообще ничего (PNG), поэтому файл после апскейла выглядит в
 *      Word/InDesign/типографии как «72 dpi» — притом что пикселей в нём стало
 *      вчетверо больше. Именно это и стоит за запросом «увеличить разрешение
 *      фото ДО 300 DPI»: людям нужна не только выросшая сетка пикселей, но и
 *      честная метка плотности, иначе приёмка макета ругается.
 *
 * ⚠️ ЧЕСТНАЯ ГРАНИЦА. dpi в файле — это ТОЛЬКО метка «печатать вот такого
 * физического размера». Она не добавляет ни одного пикселя и не улучшает
 * картинку. Поэтому UI обязан говорить обе вещи: сколько пикселей есть на
 * самом деле и что записано меткой. Модуль ничего не «подкручивает» молча.
 */
(function (global) {
    'use strict';

    var CM_PER_INCH = 2.54;

    /* ── Форматы бумаги, см. Длинная сторона второй — ориентацию под картинку
     *    подбирает orient() ниже, чтобы «A4» работал и для альбомной фотки. ── */
    var PAPER = [
        { id: '10x15',  cm: [10,   15],   kind: 'photo' },
        { id: '13x18',  cm: [13,   18],   kind: 'photo' },
        { id: '15x21',  cm: [15,   21],   kind: 'photo' },
        { id: '20x30',  cm: [20,   30],   kind: 'photo' },
        { id: '30x40',  cm: [30,   40],   kind: 'photo' },
        { id: '40x50',  cm: [40,   50],   kind: 'photo' },
        { id: '50x70',  cm: [50,   70],   kind: 'photo' },
        { id: '60x90',  cm: [60,   90],   kind: 'photo' },
        { id: 'a6',     cm: [10.5, 14.8], kind: 'paper' },
        { id: 'a5',     cm: [14.8, 21.0], kind: 'paper' },
        { id: 'a4',     cm: [21.0, 29.7], kind: 'paper' },
        { id: 'a3',     cm: [29.7, 42.0], kind: 'paper' },
        { id: 'a2',     cm: [42.0, 59.4], kind: 'paper' },
        { id: 'a1',     cm: [59.4, 84.1], kind: 'paper' },
        { id: 'a0',     cm: [84.1, 118.9], kind: 'paper' }
    ];

    /* ── Требуемая плотность по дистанции просмотра.
     *
     * Это НЕ вывод из остроты зрения. Чистая острота (1 угловая минута) даёт на
     * полутора метрах ~60 dpi, и печать по такому числу разочарует любого, кто
     * подойдёт к постеру ближе — а к постеру подходят всегда. Поэтому здесь
     * стоят числа отраслевой практики препресса, а не красивая формула:
     * фотопечать в руках — 300, рамка на столе — 240, стена — 150, большой
     * плакат — 100. Совпадение с классическим «300 dpi» на 30 см не случайно:
     * этот самый распространённый совет и есть ближний конец той же шкалы.
     *
     * `strict` — отдельный режим «типография требует 300 и точка», он нужен,
     * когда решает не глаз, а требование приёмки макета. ── */
    var DISTANCE = [
        { id: 'strict', dpi: 300, cm: null },
        { id: 'hand',   dpi: 300, cm: 30 },
        { id: 'desk',   dpi: 240, cm: 50 },
        { id: 'wall',   dpi: 150, cm: 150 },
        { id: 'poster', dpi: 100, cm: 300 }
    ];

    function paper(id) {
        for (var i = 0; i < PAPER.length; i++) if (PAPER[i].id === id) return PAPER[i];
        return null;
    }
    function distance(id) {
        for (var i = 0; i < DISTANCE.length; i++) if (DISTANCE[i].id === id) return DISTANCE[i];
        return DISTANCE[0];
    }

    /* Развернуть лист под ориентацию картинки: альбомную фотку печатают на
     * альбомно положенном A4, и требовать от пользователя выбирать «A4
     * горизонтальный» отдельным контролом — лишний шаг. */
    function orient(cmShort, cmLong, srcW, srcH) {
        var landscape = srcW >= srcH;
        return landscape ? { w: cmLong, h: cmShort } : { w: cmShort, h: cmLong };
    }

    /* Реально достижимая плотность на листе w×h см при печати «в обрез»
     * (кадр заполняет лист, лишнее обрезается). Ограничивает та сторона, где
     * пикселей на сантиметр меньше — отсюда min, а не max: печать по большей
     * стороне соврала бы в свою пользу. */
    function achievableDpi(srcW, srcH, cmW, cmH) {
        if (!srcW || !srcH || !cmW || !cmH) return 0;
        var inW = cmW / CM_PER_INCH, inH = cmH / CM_PER_INCH;
        return Math.min(srcW / inW, srcH / inH);
    }

    /* Самый большой лист (см), который файл закрывает при заданной плотности,
     * с сохранением пропорций картинки. */
    function maxPrintCm(srcW, srcH, dpi) {
        if (!srcW || !srcH || !dpi) return { w: 0, h: 0 };
        return {
            w: (srcW / dpi) * CM_PER_INCH,
            h: (srcH / dpi) * CM_PER_INCH
        };
    }

    /*
     * Главный расчёт.
     *   opts: { srcW, srcH, cmW, cmH, dpi, scales }
     *   scales — какие кратности апскейла доступны (по умолчанию 1/2/4).
     *
     * Возвращает:
     *   requiredW/H  — сколько пикселей нужно на лист
     *   nowDpi       — что файл даёт сейчас
     *   ok           — хватает ли уже сейчас
     *   shortfall    — во сколько раз не хватает (1 = хватает)
     *   scale        — минимальная кратность из scales, которой хватит
     *                  (null — не хватит даже максимальной)
     *   scaleDpi     — плотность после выбранной кратности
     *   maxNow/maxAt — максимальный лист сейчас и после апскейла, см
     */
    function evaluate(opts) {
        opts = opts || {};
        var srcW = +opts.srcW || 0, srcH = +opts.srcH || 0;
        var cmW  = +opts.cmW  || 0, cmH  = +opts.cmH  || 0;
        var dpi  = +opts.dpi  || 300;
        var scales = opts.scales || [1, 2, 4];

        var inW = cmW / CM_PER_INCH, inH = cmH / CM_PER_INCH;
        var requiredW = Math.round(inW * dpi);
        var requiredH = Math.round(inH * dpi);
        var nowDpi = achievableDpi(srcW, srcH, cmW, cmH);
        var ok = nowDpi >= dpi - 0.5;      // полпикселя допуска — округление, не подарок
        var shortfall = nowDpi > 0 ? dpi / nowDpi : Infinity;

        var pick = null;
        for (var i = 0; i < scales.length; i++) {
            if (nowDpi * scales[i] >= dpi - 0.5) { pick = scales[i]; break; }
        }
        var best = scales[scales.length - 1];

        return {
            requiredW: requiredW,
            requiredH: requiredH,
            nowDpi: nowDpi,
            ok: ok,
            shortfall: shortfall,
            scale: pick,
            scaleDpi: nowDpi * (pick || best),
            bestScale: best,
            bestDpi: nowDpi * best,
            maxNow: maxPrintCm(srcW, srcH, dpi),
            maxAt: maxPrintCm(srcW * best, srcH * best, dpi)
        };
    }

    /* ══════════════════════════════════════════════════════════════════════
     * Физическая плотность в байтах файла
     * ══════════════════════════════════════════════════════════════════════ */

    var CRC_TABLE = null;
    function crc32(bytes, from, to) {
        if (!CRC_TABLE) {
            CRC_TABLE = new Int32Array(256);
            for (var n = 0; n < 256; n++) {
                var c = n;
                for (var k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
                CRC_TABLE[n] = c;
            }
        }
        var crc = -1;
        for (var i = from; i < to; i++) crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ bytes[i]) & 0xFF];
        return (crc ^ -1) >>> 0;
    }

    /* ── Чтение ──────────────────────────────────────────────────────────── */

    /*
     * Достать плотность из готового файла. Возвращает
     *   { dpi, dpiX, dpiY, source } либо null, если метки нет.
     * source: 'jfif' | 'exif' | 'phys'.
     *
     * Смысл этой функции на странице — не «улучшить», а ОБЪЯСНИТЬ. Классическая
     * паника «у меня фото 72 dpi, в типографии не примут» лечится показом двух
     * чисел рядом: метка в файле и реальные пиксели.
     */
    function readDensity(buf) {
        var b = new Uint8Array(buf);
        if (b.length < 16) return null;
        if (b[0] === 0xFF && b[1] === 0xD8) return readJpegDensity(b);
        if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) return readPngDensity(b);
        return null;
    }

    function readJpegDensity(b) {
        var p = 2;
        while (p + 4 < b.length) {
            if (b[p] !== 0xFF) { p++; continue; }
            var marker = b[p + 1];
            if (marker === 0xD8 || marker === 0x01 || (marker >= 0xD0 && marker <= 0xD7)) { p += 2; continue; }
            if (marker === 0xDA || marker === 0xD9) break;     // пошли данные — метаданных дальше нет
            var len = (b[p + 2] << 8) | b[p + 3];
            if (len < 2) break;
            var seg = p + 4, segEnd = p + 2 + len;

            /* APP0 / JFIF — units(1) Xdensity(2) Ydensity(2) после "JFIF\0" + версии */
            if (marker === 0xE0 && segEnd - seg >= 12 &&
                b[seg] === 0x4A && b[seg + 1] === 0x46 && b[seg + 2] === 0x49 && b[seg + 3] === 0x46 && b[seg + 4] === 0x00) {
                var units = b[seg + 7];
                var dx = (b[seg + 8] << 8) | b[seg + 9];
                var dy = (b[seg + 10] << 8) | b[seg + 11];
                if (units === 1 && dx > 0) return { dpi: dx, dpiX: dx, dpiY: dy || dx, source: 'jfif' };
                if (units === 2 && dx > 0) {   // точек на сантиметр
                    return { dpi: Math.round(dx * CM_PER_INCH), dpiX: Math.round(dx * CM_PER_INCH),
                             dpiY: Math.round((dy || dx) * CM_PER_INCH), source: 'jfif' };
                }
            }

            /* APP1 / EXIF — XResolution (0x011A) + ResolutionUnit (0x0128) */
            if (marker === 0xE1 && segEnd - seg >= 14 &&
                b[seg] === 0x45 && b[seg + 1] === 0x78 && b[seg + 2] === 0x69 && b[seg + 3] === 0x66) {
                var got = readExifResolution(b, seg + 6, segEnd);
                if (got) return got;
            }
            p = segEnd;
        }
        return null;
    }

    function readExifResolution(b, tiff, end) {
        if (tiff + 8 > end) return null;
        var le = (b[tiff] === 0x49 && b[tiff + 1] === 0x49);
        function u16(o) { return le ? (b[o] | (b[o + 1] << 8)) : ((b[o] << 8) | b[o + 1]); }
        function u32(o) {
            return le ? ((b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0)
                      : (((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0);
        }
        if (u16(tiff + 2) !== 0x002A) return null;
        var ifd = tiff + u32(tiff + 4);
        if (ifd + 2 > end) return null;
        var count = u16(ifd), xres = 0, unit = 2;
        for (var i = 0; i < count; i++) {
            var e = ifd + 2 + i * 12;
            if (e + 12 > end) break;
            var tag = u16(e);
            if (tag === 0x011A) {                       // XResolution, RATIONAL
                var off = tiff + u32(e + 8);
                if (off + 8 <= end) {
                    var num = u32(off), den = u32(off + 4);
                    if (den) xres = num / den;
                }
            } else if (tag === 0x0128) {                // ResolutionUnit: 2=inch 3=cm
                unit = u16(e + 8);
            }
        }
        if (!xres) return null;
        var dpi = unit === 3 ? Math.round(xres * CM_PER_INCH) : Math.round(xres);
        return dpi > 0 ? { dpi: dpi, dpiX: dpi, dpiY: dpi, source: 'exif' } : null;
    }

    function readPngDensity(b) {
        var p = 8;
        while (p + 8 <= b.length) {
            var len = ((b[p] << 24) | (b[p + 1] << 16) | (b[p + 2] << 8) | b[p + 3]) >>> 0;
            var type = String.fromCharCode(b[p + 4], b[p + 5], b[p + 6], b[p + 7]);
            if (type === 'pHYs' && len === 9) {
                var d = p + 8;
                var ppuX = ((b[d] << 24) | (b[d + 1] << 16) | (b[d + 2] << 8) | b[d + 3]) >>> 0;
                var ppuY = ((b[d + 4] << 24) | (b[d + 5] << 16) | (b[d + 6] << 8) | b[d + 7]) >>> 0;
                if (b[d + 8] !== 1 || !ppuX) return null;   // unit≠метр — соотношение сторон, не плотность
                return {
                    dpi: Math.round(ppuX * 0.0254),
                    dpiX: Math.round(ppuX * 0.0254),
                    dpiY: Math.round((ppuY || ppuX) * 0.0254),
                    source: 'phys'
                };
            }
            if (type === 'IDAT' || type === 'IEND') break;
            p += 12 + len;
        }
        return null;
    }

    /* ── Запись ──────────────────────────────────────────────────────────── */

    /*
     * Проставить dpi в готовый JPEG. Canvas всегда выдаёт APP0/JFIF с
     * units=0 (плотность неизвестна, «72») — тогда патчим три поля на месте.
     * Если APP0 почему-то нет (нестандартный кодек) — вставляем свой сегмент
     * сразу за SOI; это законный JPEG, JFIF обязан идти первым.
     */
    function writeJpegDensity(bytes, dpi) {
        var b = bytes;
        if (b[0] !== 0xFF || b[1] !== 0xD8) return b;
        var hasApp0 = b[2] === 0xFF && b[3] === 0xE0 &&
                      b[6] === 0x4A && b[7] === 0x46 && b[8] === 0x49 && b[9] === 0x46 && b[10] === 0x00;
        if (hasApp0) {
            var out = new Uint8Array(b);            // копия: исходник может быть чужим буфером
            out[13] = 1;                            // units = dots per inch
            out[14] = (dpi >> 8) & 0xFF; out[15] = dpi & 0xFF;
            out[16] = (dpi >> 8) & 0xFF; out[17] = dpi & 0xFF;
            return out;
        }
        var app0 = new Uint8Array([
            0xFF, 0xE0, 0x00, 0x10, 0x4A, 0x46, 0x49, 0x46, 0x00, 0x01, 0x02,
            0x01, (dpi >> 8) & 0xFF, dpi & 0xFF, (dpi >> 8) & 0xFF, dpi & 0xFF, 0x00, 0x00
        ]);
        var res = new Uint8Array(b.length + app0.length);
        res.set(b.subarray(0, 2), 0);
        res.set(app0, 2);
        res.set(b.subarray(2), 2 + app0.length);
        return res;
    }

    /*
     * Проставить dpi в PNG. pHYs хранит пиксели на МЕТР, поэтому dpi гоняем
     * через 0.0254. Чанк по спецификации должен стоять до первого IDAT —
     * ставим сразу за IHDR, а прежний pHYs (если Canvas когда-нибудь начнёт
     * его писать) выкидываем, чтобы не оставить две противоречащие метки.
     */
    function writePngDensity(bytes, dpi) {
        var b = bytes;
        if (b[0] !== 0x89 || b[1] !== 0x50) return b;
        var ppu = Math.round(dpi / 0.0254);

        var chunk = new Uint8Array(21);
        var dv = new DataView(chunk.buffer);
        dv.setUint32(0, 9);
        chunk[4] = 0x70; chunk[5] = 0x48; chunk[6] = 0x59; chunk[7] = 0x73;   // 'pHYs'
        dv.setUint32(8, ppu); dv.setUint32(12, ppu);
        chunk[16] = 1;                                                        // unit = метр
        dv.setUint32(17, crc32(chunk, 4, 17));

        /* Собрать заново, пропустив старый pHYs. */
        var parts = [b.subarray(0, 8)], p = 8, inserted = false;
        while (p + 8 <= b.length) {
            var len = ((b[p] << 24) | (b[p + 1] << 16) | (b[p + 2] << 8) | b[p + 3]) >>> 0;
            var type = String.fromCharCode(b[p + 4], b[p + 5], b[p + 6], b[p + 7]);
            var end = p + 12 + len;
            if (end > b.length) { parts.push(b.subarray(p)); p = b.length; break; }
            if (type !== 'pHYs') parts.push(b.subarray(p, end));
            if (type === 'IHDR' && !inserted) { parts.push(chunk); inserted = true; }
            p = end;
        }
        if (!inserted) return b;                    // не PNG-структура — не трогаем

        var total = 0, i;
        for (i = 0; i < parts.length; i++) total += parts[i].length;
        var res = new Uint8Array(total), off = 0;
        for (i = 0; i < parts.length; i++) { res.set(parts[i], off); off += parts[i].length; }
        return res;
    }

    /*
     * Blob → Blob с проставленной плотностью. WebP не поддерживается: в его
     * контейнере нет поля физической плотности, и притворяться, что оно есть,
     * значит врать пользователю — возвращаем исходный blob как есть, а UI
     * обязан сказать, что для WebP метка не пишется.
     */
    function stampDensity(blob, dpi, mime) {
        mime = mime || blob.type || '';
        if (!dpi || dpi <= 0) return Promise.resolve(blob);
        if (mime.indexOf('jpeg') < 0 && mime.indexOf('jpg') < 0 && mime.indexOf('png') < 0) {
            return Promise.resolve(blob);
        }
        return blob.arrayBuffer().then(function (buf) {
            var b = new Uint8Array(buf);
            var out = (mime.indexOf('png') >= 0) ? writePngDensity(b, dpi) : writeJpegDensity(b, dpi);
            return new Blob([out], { type: mime });
        }).catch(function () { return blob; });
    }

    /* ── Максимальный канвас браузера ──────────────────────────────────────
     *
     * Превышение не бросает исключение — канвас молча пустой, toBlob отдаёт
     * null. Число — ТОЛЬКО из canvasLimits() (public/js/canvas-limits.js, один
     * владелец на сайт), модулем по import(), как в report-pdf.js.
     *
     * ⛔ Не мерить выделением. До 22.10.2026 здесь был живой поиск с холстом
     * 16384² первым кандидатом: /photo-enhancer при КАЖДОМ входе брал на ~1 с
     * +1,1 ГБ памяти GPU (замер Edge 155), а на ноутбуке с 4 ГБ и встроенной
     * графикой память GPU — это его единственная ОЗУ. Соседний зонд того же
     * рода (merge-images, до 4 ГБ) там вешал машину целиком. Разбор —
     * docs/why/primitives/canvas-ceiling.md.
     *
     * Пока модуль не пришёл (миллисекунды после загрузки), ответ — пол iOS ≤ 17
     * (16,7 Мпикс): ни одно устройство его не отвергает. `limitsReady` — когда
     * пришёл: потребитель, спросивший раньше, переспрашивает.
     * ──────────────────────────────────────────────────────────────────── */
    var FLOOR_AREA = 16777216;
    var _limits = null;
    var limitsReady = (function () {
        try {
            var src = typeof global.__jsSrc === 'function' ? global.__jsSrc('/js/canvas-limits.js') : '/js/canvas-limits.js';
            return import(src).then(function (m) {
                try { _limits = m && typeof m.canvasLimits === 'function' ? m.canvasLimits() : null; } catch (e) { _limits = null; }
                return _limits;
            }, function () { return null; });
        } catch (e) { return Promise.resolve(null); }
    })();
    function maxCanvasArea() {
        return _limits && _limits.area > 0 ? _limits.area : FLOOR_AREA;
    }

    global.PrintFit = {
        CM_PER_INCH: CM_PER_INCH,
        PAPER: PAPER,
        DISTANCE: DISTANCE,
        paper: paper,
        distance: distance,
        orient: orient,
        achievableDpi: achievableDpi,
        maxPrintCm: maxPrintCm,
        evaluate: evaluate,
        readDensity: readDensity,
        stampDensity: stampDensity,
        maxCanvasArea: maxCanvasArea,
        limitsReady: limitsReady,
        _writeJpegDensity: writeJpegDensity,
        _writePngDensity: writePngDensity
    };
})(typeof window !== 'undefined' ? window : this);
